import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  GenerateOtpDto,
  PhoneLoginDto,
  PhoneSignupDto,
  SignupAvailabilityDto,
  ValidateOtpDto,
  VerifyEmailConfirmDto,
} from '@nomiss/sdk';
import type { LoginDto } from '@nomiss/sdk/models/LoginDto';
import {
  authService,
  checkLoginAvailability,
  checkSignupAvailability,
  confirmVerifyEmailOtp,
  emailLogin,
  type EmailLoginPayload,
  generateEmailOtp,
  loginWithPassword,
  phoneLogin,
  phoneSignup,
  sendVerifyEmailOtp,
} from '@nomiss/services';
import { myProfileQueryKey } from './use-users';

export const useMobileLogin = () => {
  const mobileLoginMutation = useMutation({
    mutationFn: (loginData: LoginDto) => loginWithPassword(loginData),
  });
  return { mobileLoginMutation };
};

export const useGenerateOtp = () => {
  const generateOtpMutation = useMutation({
    mutationFn: (otpData: GenerateOtpDto) => authService.generateOtp(otpData),
  });

  return { generateOtpMutation };
};

export const useValidateOtp = () => {
  const validateOtpMutation = useMutation({
    mutationFn: (otpData: ValidateOtpDto) => authService.validateOtp(otpData),
  });

  return { validateOtpMutation };
};

export const usePhoneSignup = () => {
  const phoneSignupMutation = useMutation({
    mutationFn: (dto: PhoneSignupDto) => phoneSignup(dto),
  });

  return { phoneSignupMutation };
};

export const usePhoneLogin = () => {
  const phoneLoginMutation = useMutation({
    mutationFn: (dto: PhoneLoginDto) => phoneLogin(dto),
  });

  return { phoneLoginMutation };
};

export const useLoginAvailability = () => {
  const loginAvailabilityMutation = useMutation({
    mutationFn: (payload: { phone?: string; email?: string }) =>
      checkLoginAvailability(payload),
  });

  return { loginAvailabilityMutation };
};

export const useSignupAvailability = () => {
  const signupAvailabilityMutation = useMutation({
    mutationFn: (payload: SignupAvailabilityDto) =>
      checkSignupAvailability(payload),
  });

  return { signupAvailabilityMutation };
};

export const useGenerateEmailOtp = () => {
  const generateEmailOtpMutation = useMutation({
    mutationFn: (payload: { email: string; boundPhone?: string }) =>
      generateEmailOtp(payload),
  });

  return { generateEmailOtpMutation };
};

export const useEmailLogin = () => {
  const emailLoginMutation = useMutation({
    mutationFn: (payload: EmailLoginPayload) => emailLogin(payload),
  });

  return { emailLoginMutation };
};

export const useSendVerifyEmailOtp = () => {
  const sendVerifyEmailOtpMutation = useMutation({
    mutationFn: () => sendVerifyEmailOtp(),
  });

  return { sendVerifyEmailOtpMutation };
};

export const useConfirmVerifyEmailOtp = () => {
  const queryClient = useQueryClient();

  const confirmVerifyEmailOtpMutation = useMutation({
    mutationFn: (payload: VerifyEmailConfirmDto) =>
      confirmVerifyEmailOtp(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myProfileQueryKey });
    },
  });

  return { confirmVerifyEmailOtpMutation };
};
